import React, {useState, useEffect} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {LocalStorage} from '../../../utils/LocalStorage';
import {AccessService} from '../../../utils/AccessService';
import {PreviewProvider} from '../../../context/PreviewContext';
import {PreviewBanner} from './PreviewBanner';
import {Spinner} from '../../Spinner';
import {PortalHome} from '../../portal/PortalHome';
import {PortalDashboMCP} from '../../portal/PortalDashboMCP';
import {PortalConnections} from '../../portal/PortalConnections';
import {PreviewPortalReports} from './PreviewPortalReports';
import {resolvePreviewBranding} from './previewBranding';
import {notifyPreviewReady, readPreviewBrandingMessage} from '../portalBrandSource';

// Mapeo de las capacidades del portal (AgencyClient.portal_default_features) a los módulos
// de acceso que espera LayoutPortal para el nav del portal.
const PORTAL_MODULE_TO_ACCESS = {
    dashboard: AccessService.MODULE_MARKETING,
    reports: AccessService.MODULE_REPORTS,
    connect: AccessService.MODULE_DASHBO_MCP,
    connections: AccessService.MODULE_CLIENT_CONNECTIONS,
};

// Qué módulo(s) del plan de la agencia habilitan cada capability del portal. Default: el
// mismo MODULE_*. Excepción "reports": la capability "Vistas del Explorador" la habilita a
// nivel agencia el Explorador (DATA_EXPLORER), NO el reporteador con IA (REPORTS) — aceptamos
// cualquiera de los dos (mismo criterio que AccessService en el backend). Sin esto, una
// agencia con DATA_EXPLORER pero sin REPORTS no veía la sección en la preview.
const PORTAL_MODULE_AGENCY_REQUIREMENT = {
    dashboard: [AccessService.MODULE_MARKETING],
    reports: [AccessService.MODULE_REPORTS, AccessService.MODULE_DATA_EXPLORER],
    connect: [AccessService.MODULE_DASHBO_MCP],
    connections: [AccessService.MODULE_CLIENT_CONNECTIONS],
};

// Fallback permisivo si el backend no devuelve `modules` (compat con versión vieja).
const ALL_MODULES_ON = { dashboard: true, reports: true, connect: true, connections: true };

const getSectionFromPath = (pathname, clientId) => {
    const prefix = `/client/${clientId}/preview`;
    const rest = pathname.replace(prefix, '');
    if (rest === '/dashbo-connect' || rest === '/dashbo-mcp') return 'dashbo-mcp';
    if (rest === '/connections') return 'connections';
    if (rest === '/reports') return 'reports';
    return 'home';
};

const SectionContent = ({section, clientId}) => {
    switch (section) {
        case 'dashbo-mcp': return <PortalDashboMCP/>;
        case 'connections': return <PortalConnections/>;
        case 'reports': return <PreviewPortalReports clientId={clientId}/>;
        default: return <PortalHome/>;
    }
};

export const ClientPortalPreview = () => {
    const {id} = useParams();
    const location = useLocation();

    const [clientName, setClientName] = useState('');
    const [agencyModules, setAgencyModules] = useState(null);
    const [portalModules, setPortalModules] = useState(null);
    const [branding, setBranding] = useState(null);
    // La marca de la AGENCIA, de la que hereda el cliente sin override propio. `undefined` =
    // todavía no llegó: la vista previa espera, para no dibujar primero la marca de Dashbo y
    // cambiarla al instante. `null` = el pedido falló, y se sigue con la del cliente.
    const [agencyBranding, setAgencyBranding] = useState(undefined);
    // El borrador de la marca que manda la zona Apariencia mientras el usuario elige (antes de guardar).
    // `null` = no hay borrador: se muestra lo guardado.
    const [draftBranding, setDraftBranding] = useState(null);

    const section = getSectionFromPath(location.pathname, id);
    // La zona Apariencia pasa el tema seleccionado por la URL (`?theme=`). Es la fuente
    // autoritativa para la "vista previa en vivo": refleja la selección al instante y de
    // forma determinística, sin depender de que el GET de config recién persistido no
    // venga cacheado. Si no viene en la URL (preview abierto en pestaña aparte), se usa
    // el tema persistido del fetch.
    const themeFromUrl = new URLSearchParams(location.search).get('theme');
    // Embebido = el preview corre dentro de un iframe (zona Apariencia) → sin el banner de
    // "Vista previa" (es redundante ahí). Abierto en pestaña aparte (botón "Ver portal como
    // cliente") no hay iframe → se muestra el banner. Detectarlo por iframe es más robusto
    // que un query param (sobrevive cualquier redirect que pudiera limpiar la URL).
    let isEmbed = false;
    try {
        isEmbed = window.self !== window.top;
    } catch (e) {
        isEmbed = true; // si el acceso a window.top falla (cross-origin), asumimos embebido
    }

    // Embebido, escucha el borrador de la marca que le manda la zona Apariencia (ronda 2 de la tanda
    // iVirtual: «antes de guardar, el cambio se ve en la vista previa»). El padre puede haber mandado
    // el borrador antes de que este listener exista, así que al engancharlo se le avisa que ya escucha
    // y el padre lo vuelve a mandar. Sólo se acepta lo que llega del padre y del mismo origen.
    useEffect(() => {
        if (!isEmbed) return undefined;
        const onMessage = (event) => {
            const draft = readPreviewBrandingMessage(event);
            if (draft) setDraftBranding(draft);
        };
        window.addEventListener('message', onMessage);
        notifyPreviewReady();
        return () => window.removeEventListener('message', onMessage);
    }, [isEmbed]);

    // Obtener datos del cliente (nombre) + config del portal (branding + módulos activos)
    useEffect(() => {
        new APIClient(API_URL + 'client/' + id).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setClientName(data.data.name || '');
            }
        });

        new APIClient(API_URL + 'client/' + id + '/portal/config/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setBranding({...data.data.branding, portal_theme: themeFromUrl || data.data.portal_theme});
                setPortalModules(data.data.modules || ALL_MODULES_ON);
            } else {
                setPortalModules(ALL_MODULES_ON);
            }
        }).catch(() => setPortalModules(ALL_MODULES_ON));
    }, [id, themeFromUrl]);

    // El portal real le muestra al cliente la marca RESUELTA (su override y, si no tiene, la de la
    // agencia), pero `portal/config/` trae sólo el override: sin esto, un cliente sin marca propia
    // se veía con el logo y el nombre de Dashbo (card E5J1Cqzu). `cache` comparte el pedido con los
    // otros callers del mismo endpoint.
    useEffect(() => {
        new APIClient(API_URL + 'get-agency-data/').call('GET', {}, {cache: true}).then((data) => {
            setAgencyBranding(data && data.status === 'OK' && data.general_data ? data.general_data : null);
        }).catch(() => setAgencyBranding(null));
    }, []);

    // Obtener módulos disponibles para la agencia (un módulo no puede mostrarse si la agencia
    // no lo tiene en su plan, aunque esté activado en el portal del cliente).
    useEffect(() => {
        const userId = LocalStorage.fetchUserId();
        if (!userId) {
            setAgencyModules([]);
            return;
        }
        AccessService.GetUserModules(userId, (data) => {
            setAgencyModules(data.modules || []);
        }, () => setAgencyModules([]));
    }, []);

    if (agencyModules === null || portalModules === null || agencyBranding === undefined) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <Spinner color="positive"/>
            </div>
        );
    }

    // Módulos del preview = capabilities activadas en el portal cuya feature la agencia tiene
    // en su plan. La salida usa los MODULE_* que espera LayoutPortal para el nav.
    const previewModules = Object.entries(portalModules)
        .filter(([, on]) => on)
        .filter(([key]) => (PORTAL_MODULE_AGENCY_REQUIREMENT[key] || []).some(m => agencyModules.includes(m)))
        .map(([key]) => PORTAL_MODULE_TO_ACCESS[key])
        .filter(Boolean);

    const previewValue = {
        isPreview: true,
        previewClientId: id,
        previewModules: previewModules,
        previewBranding: resolvePreviewBranding(
            draftBranding ? {...(branding || {}), ...draftBranding} : branding, agencyBranding),
    };

    // `-with-banner` corre hacia abajo el header del portal, que también es sticky: sin eso, al
    // hacer scroll los dos se pegan arriba y la banda lo tapa.
    return (
        <PreviewProvider value={previewValue}>
            <div className={`preview-wrapper${isEmbed ? '' : ' -with-banner'}`}>
                {!isEmbed && <PreviewBanner clientId={id} clientName={clientName}/>}
                <SectionContent section={section} clientId={id}/>
            </div>
        </PreviewProvider>
    );
};
